import React, { useState, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  FileText, Download, Printer, CheckCircle, Shield, Scale, Building2,
  Calendar, DollarSign, Users, Lock, FileCheck, AlertCircle, ChevronDown, ChevronUp
} from 'lucide-react';
import { toast } from 'sonner';

export const ContratoPage = () => {
  const [orgName, setOrgName] = useState('');
  const [cnpj, setCnpj] = useState('');
  const [representante, setRepresentante] = useState('');
  const [cargo, setCargo] = useState('');
  const [valor, setValor] = useState('');
  const [assentos, setAssentos] = useState('');
  const [expandedSection, setExpandedSection] = useState(null);
  const contractRef = useRef(null);

  const handlePrint = () => {
    window.print();
  };

  const handleDownload = () => {
    const content = contractRef.current?.innerText;
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `contrato_bluegov_${orgName.replace(/\s/g, '_') || 'modelo'}.txt`;
    a.click();
    toast.success('Contrato baixado com sucesso');
  };

  const today = new Date().toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' });

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50/30">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-12 px-8">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <Scale className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-3xl font-bold">Contrato de Prestação de Serviços</h1>
              <p className="text-blue-200">Modelo Híbrido - SaaS + Serviços</p>
            </div>
          </div>
          <div className="flex items-center gap-6 mt-6">
            <Badge className="bg-white/20 text-white border-white/30">Lei 14.133/2021</Badge>
            <Badge className="bg-white/20 text-white border-white/30">LGPD Compliant</Badge>
            <Badge className="bg-white/20 text-white border-white/30">Pronto para Aceite</Badge>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto py-8 px-8">
        {/* Form to customize */}
        <Card className="mb-8 border-blue-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-blue-600" />
              Dados do Contratante
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-4">
              <Input placeholder="Nome do Órgão Público" value={orgName} onChange={(e) => setOrgName(e.target.value)} />
              <Input placeholder="CNPJ" value={cnpj} onChange={(e) => setCnpj(e.target.value)} />
              <Input placeholder="Nome do Representante Legal" value={representante} onChange={(e) => setRepresentante(e.target.value)} />
              <Input placeholder="Cargo do Representante" value={cargo} onChange={(e) => setCargo(e.target.value)} />
              <Input placeholder="Valor Mensal (R$)" type="number" value={valor} onChange={(e) => setValor(e.target.value)} />
              <Input placeholder="Quantidade de Assentos" type="number" value={assentos} onChange={(e) => setAssentos(e.target.value)} />
            </div>
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex items-center justify-end gap-4 mb-8">
          <Button variant="outline" onClick={handlePrint}>
            <Printer className="w-4 h-4 mr-2" />Imprimir
          </Button>
          <Button onClick={handleDownload}>
            <Download className="w-4 h-4 mr-2" />Baixar Contrato
          </Button>
        </div>

        {/* Contract Document */}
        <Card className="shadow-xl" ref={contractRef}>
          <CardContent className="p-12 prose prose-slate max-w-none">
            {/* Header */}
            <div className="text-center mb-12 border-b pb-8">
              <h1 className="text-2xl font-bold text-slate-900 mb-2">CONTRATO DE PRESTAÇÃO DE SERVIÇOS DE TECNOLOGIA</h1>
              <h2 className="text-lg text-slate-600 mb-4">MODELO HÍBRIDO: LICENCIAMENTO SaaS + SERVIÇOS ESPECIALIZADOS</h2>
              <p className="text-sm text-slate-500">Compatível com a Lei nº 14.133/2021 (Nova Lei de Licitações)</p>
            </div>

            {/* Partes */}
            <section className="mb-8">
              <h3 className="text-lg font-bold text-slate-900 mb-4">DAS PARTES</h3>
              <p className="text-slate-700 leading-relaxed">
                <strong>CONTRATANTE:</strong> {orgName || '[NOME DO ÓRGÃO PÚBLICO]'}, inscrito no CNPJ sob o nº {cnpj || '[CNPJ]'}, 
                com sede em [ENDEREÇO COMPLETO], neste ato representado por seu {cargo || '[CARGO]'}, 
                Sr(a). {representante || '[NOME DO REPRESENTANTE]'}, doravante denominado simplesmente CONTRATANTE.
              </p>
              <p className="text-slate-700 leading-relaxed mt-4">
                <strong>CONTRATADA:</strong> BLUEGOV TECNOLOGIA LTDA., inscrita no CNPJ sob o nº XX.XXX.XXX/0001-XX, 
                com sede na [ENDEREÇO], neste ato representada por seu Diretor, Sr. [NOME], 
                doravante denominada simplesmente CONTRATADA ou BLUEGOV.
              </p>
            </section>

            {/* Cláusulas */}
            <section className="mb-8">
              <h3 className="text-lg font-bold text-slate-900 mb-4 cursor-pointer flex items-center justify-between" onClick={() => toggleSection('objeto')}>
                CLÁUSULA PRIMEIRA - DO OBJETO
                {expandedSection === 'objeto' ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </h3>
              <div className={expandedSection === 'objeto' || expandedSection === null ? '' : 'hidden'}>
                <p className="text-slate-700 leading-relaxed">
                  1.1. O presente contrato tem por objeto a prestação de serviços de tecnologia pela CONTRATADA ao CONTRATANTE, 
                  compreendendo, de forma híbrida e complementar:
                </p>
                <p className="text-slate-700 leading-relaxed mt-2 ml-4">
                  <strong>a) LICENCIAMENTO SaaS:</strong> Cessão de direito de uso da plataforma BlueGov AI, sistema de gestão 
                  inteligente de atos de controle governamental, disponibilizado em modelo de Software como Serviço (SaaS), 
                  acessível via internet, incluindo todas as funcionalidades descritas no Anexo V;
                </p>
                <p className="text-slate-700 leading-relaxed mt-2 ml-4">
                  <strong>b) SERVIÇOS ESPECIALIZADOS:</strong> Prestação de serviços técnicos de implantação, configuração, 
                  treinamento, suporte técnico, manutenção evolutiva e corretiva, transferência de conhecimento e assessoria 
                  especializada em gestão de atos de controle.
                </p>
                <p className="text-slate-700 leading-relaxed mt-4">
                  1.2. A quantidade de licenças (assentos) contratadas é de {assentos || '[QUANTIDADE]'} ({assentos ? 'número por extenso' : '[POR EXTENSO]'}) 
                  usuários simultâneos, podendo ser ampliada mediante termo aditivo.
                </p>
              </div>
            </section>

            <section className="mb-8">
              <h3 className="text-lg font-bold text-slate-900 mb-4 cursor-pointer flex items-center justify-between" onClick={() => toggleSection('natureza')}>
                CLÁUSULA SEGUNDA - DA NATUREZA HÍBRIDA
                {expandedSection === 'natureza' ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </h3>
              <div className={expandedSection === 'natureza' || expandedSection === null ? '' : 'hidden'}>
                <p className="text-slate-700 leading-relaxed">
                  2.1. O presente contrato possui natureza jurídica híbrida, combinando elementos de:
                </p>
                <p className="text-slate-700 leading-relaxed mt-2 ml-4">
                  a) <strong>Licenciamento de Software (SaaS):</strong> Caracterizado pela cessão não exclusiva de direito de uso 
                  da plataforma tecnológica, sem transferência de propriedade intelectual, conforme art. 9º da Lei nº 9.609/1998;
                </p>
                <p className="text-slate-700 leading-relaxed mt-2 ml-4">
                  b) <strong>Prestação de Serviços:</strong> Caracterizado pela execução de atividades técnicas especializadas, 
                  com emprego de conhecimento técnico e mão de obra qualificada.
                </p>
                <p className="text-slate-700 leading-relaxed mt-4">
                  2.2. A contratação enquadra-se no art. 75, inciso II, da Lei nº 14.133/2021, podendo ser realizada por 
                  dispensa de licitação quando o valor estiver dentro dos limites legais, ou por inexigibilidade quando 
                  demonstrada a singularidade técnica da solução.
                </p>
              </div>
            </section>

            <section className="mb-8">
              <h3 className="text-lg font-bold text-slate-900 mb-4 cursor-pointer flex items-center justify-between" onClick={() => toggleSection('obrigacoes_contratada')}>
                CLÁUSULA TERCEIRA - DAS OBRIGAÇÕES DA CONTRATADA
                {expandedSection === 'obrigacoes_contratada' ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </h3>
              <div className={expandedSection === 'obrigacoes_contratada' || expandedSection === null ? '' : 'hidden'}>
                <p className="text-slate-700 leading-relaxed">São obrigações da CONTRATADA:</p>
                <p className="text-slate-700 leading-relaxed mt-2 ml-4">
                  3.1. Disponibilizar a plataforma BlueGov AI em ambiente de nuvem seguro, com disponibilidade mínima de 99,5% (SLA);
                </p>
                <p className="text-slate-700 leading-relaxed mt-2 ml-4">
                  3.2. Realizar a implantação inicial, incluindo configuração do ambiente, migração de dados e parametrização;
                </p>
                <p className="text-slate-700 leading-relaxed mt-2 ml-4">
                  3.3. Executar treinamento presencial ou remoto para todos os usuários indicados pelo CONTRATANTE;
                </p>
                <p className="text-slate-700 leading-relaxed mt-2 ml-4">
                  3.4. Prestar suporte técnico em dias úteis, das 8h às 18h, com tempo de resposta conforme Matriz de SLA (Anexo VI);
                </p>
                <p className="text-slate-700 leading-relaxed mt-2 ml-4">
                  3.5. Realizar manutenção evolutiva, incluindo atualizações de funcionalidades e correções de segurança;
                </p>
                <p className="text-slate-700 leading-relaxed mt-2 ml-4">
                  3.6. Garantir backup diário dos dados e retenção mínima de 90 dias;
                </p>
                <p className="text-slate-700 leading-relaxed mt-2 ml-4">
                  3.7. Manter sigilo absoluto sobre todas as informações do CONTRATANTE;
                </p>
                <p className="text-slate-700 leading-relaxed mt-2 ml-4">
                  3.8. Cumprir integralmente a Lei Geral de Proteção de Dados (Lei nº 13.709/2018);
                </p>
                <p className="text-slate-700 leading-relaxed mt-2 ml-4">
                  3.9. Fornecer relatórios mensais de uso e desempenho da plataforma;
                </p>
                <p className="text-slate-700 leading-relaxed mt-2 ml-4">
                  3.10. Executar a transferência de conhecimento conforme Anexo VII.
                </p>
              </div>
            </section>

            <section className="mb-8">
              <h3 className="text-lg font-bold text-slate-900 mb-4 cursor-pointer flex items-center justify-between" onClick={() => toggleSection('obrigacoes_contratante')}>
                CLÁUSULA QUARTA - DAS OBRIGAÇÕES DO CONTRATANTE
                {expandedSection === 'obrigacoes_contratante' ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </h3>
              <div className={expandedSection === 'obrigacoes_contratante' || expandedSection === null ? '' : 'hidden'}>
                <p className="text-slate-700 leading-relaxed">São obrigações do CONTRATANTE:</p>
                <p className="text-slate-700 leading-relaxed mt-2 ml-4">
                  4.1. Efetuar os pagamentos nas datas e condições pactuadas;
                </p>
                <p className="text-slate-700 leading-relaxed mt-2 ml-4">
                  4.2. Designar gestor e fiscal do contrato, nos termos do art. 117 da Lei nº 14.133/2021;
                </p>
                <p className="text-slate-700 leading-relaxed mt-2 ml-4">
                  4.3. Indicar os usuários que terão acesso à plataforma;
                </p>
                <p className="text-slate-700 leading-relaxed mt-2 ml-4">
                  4.4. Fornecer as informações e documentos necessários à execução dos serviços;
                </p>
                <p className="text-slate-700 leading-relaxed mt-2 ml-4">
                  4.5. Zelar pela segurança das credenciais de acesso;
                </p>
                <p className="text-slate-700 leading-relaxed mt-2 ml-4">
                  4.6. Comunicar imediatamente qualquer irregularidade ou falha detectada;
                </p>
                <p className="text-slate-700 leading-relaxed mt-2 ml-4">
                  4.7. Atestar a execução dos serviços para fins de pagamento.
                </p>
              </div>
            </section>

            <section className="mb-8">
              <h3 className="text-lg font-bold text-slate-900 mb-4 cursor-pointer flex items-center justify-between" onClick={() => toggleSection('prazo')}>
                CLÁUSULA QUINTA - DO PRAZO
                {expandedSection === 'prazo' ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </h3>
              <div className={expandedSection === 'prazo' || expandedSection === null ? '' : 'hidden'}>
                <p className="text-slate-700 leading-relaxed">
                  5.1. O presente contrato terá vigência de 12 (doze) meses, contados a partir da data de sua assinatura, 
                  podendo ser prorrogado por iguais e sucessivos períodos até o limite de 10 (dez) anos, nos termos do 
                  art. 107 da Lei nº 14.133/2021.
                </p>
                <p className="text-slate-700 leading-relaxed mt-4">
                  5.2. A prorrogação dependerá de manifestação expressa das partes, com antecedência mínima de 60 (sessenta) dias 
                  do término da vigência.
                </p>
              </div>
            </section>

            <section className="mb-8">
              <h3 className="text-lg font-bold text-slate-900 mb-4 cursor-pointer flex items-center justify-between" onClick={() => toggleSection('valor')}>
                CLÁUSULA SEXTA - DO VALOR E PAGAMENTO
                {expandedSection === 'valor' ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </h3>
              <div className={expandedSection === 'valor' || expandedSection === null ? '' : 'hidden'}>
                <p className="text-slate-700 leading-relaxed">
                  6.1. O valor mensal do presente contrato é de R$ {valor ? parseFloat(valor).toLocaleString('pt-BR', { minimumFractionDigits: 2 }) : '[VALOR]'} 
                  ({valor ? 'valor por extenso' : '[POR EXTENSO]'}), totalizando R$ {valor ? (parseFloat(valor) * 12).toLocaleString('pt-BR', { minimumFractionDigits: 2 }) : '[VALOR ANUAL]'} 
                  ({valor ? 'valor por extenso' : '[POR EXTENSO]'}) para o período de 12 meses.
                </p>
                <p className="text-slate-700 leading-relaxed mt-4">
                  6.2. O pagamento será efetuado mensalmente, até o 10º (décimo) dia útil do mês subsequente ao da prestação 
                  dos serviços, mediante apresentação de nota fiscal devidamente atestada pelo fiscal do contrato.
                </p>
                <p className="text-slate-700 leading-relaxed mt-4">
                  6.3. Nos casos de eventuais atrasos de pagamento, o valor devido deverá ser acrescido de juros moratórios 
                  de 1% (um por cento) ao mês, apurados desde a data prevista para o pagamento até a data da sua efetiva realização.
                </p>
              </div>
            </section>

            <section className="mb-8">
              <h3 className="text-lg font-bold text-slate-900 mb-4 cursor-pointer flex items-center justify-between" onClick={() => toggleSection('reajuste')}>
                CLÁUSULA SÉTIMA - DO REAJUSTE
                {expandedSection === 'reajuste' ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </h3>
              <div className={expandedSection === 'reajuste' || expandedSection === null ? '' : 'hidden'}>
                <p className="text-slate-700 leading-relaxed">
                  7.1. Os preços contratados poderão ser reajustados após 12 (doze) meses da data-base do orçamento estimado, 
                  utilizando-se como índice o IPCA/IBGE ou outro índice oficial que vier a substituí-lo.
                </p>
                <p className="text-slate-700 leading-relaxed mt-4">
                  7.2. O reajuste será solicitado pela CONTRATADA mediante requerimento formal, acompanhado da demonstração 
                  analítica da variação do índice.
                </p>
              </div>
            </section>

            <section className="mb-8">
              <h3 className="text-lg font-bold text-slate-900 mb-4 cursor-pointer flex items-center justify-between" onClick={() => toggleSection('penalidades')}>
                CLÁUSULA OITAVA - DAS PENALIDADES
                {expandedSection === 'penalidades' ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </h3>
              <div className={expandedSection === 'penalidades' || expandedSection === null ? '' : 'hidden'}>
                <p className="text-slate-700 leading-relaxed">
                  8.1. O descumprimento das obrigações contratuais sujeitará a parte infratora às seguintes penalidades, 
                  nos termos dos arts. 155 a 163 da Lei nº 14.133/2021:
                </p>
                <p className="text-slate-700 leading-relaxed mt-2 ml-4">
                  a) Advertência;
                </p>
                <p className="text-slate-700 leading-relaxed mt-2 ml-4">
                  b) Multa de até 10% (dez por cento) sobre o valor do contrato;
                </p>
                <p className="text-slate-700 leading-relaxed mt-2 ml-4">
                  c) Suspensão temporária de participação em licitação e impedimento de contratar com a Administração;
                </p>
                <p className="text-slate-700 leading-relaxed mt-2 ml-4">
                  d) Declaração de inidoneidade para licitar ou contratar com a Administração Pública.
                </p>
                <p className="text-slate-700 leading-relaxed mt-4">
                  8.2. As penalidades serão aplicadas após regular processo administrativo, garantidos o contraditório e a ampla defesa.
                </p>
              </div>
            </section>

            <section className="mb-8">
              <h3 className="text-lg font-bold text-slate-900 mb-4 cursor-pointer flex items-center justify-between" onClick={() => toggleSection('lgpd')}>
                CLÁUSULA NONA - DA PROTEÇÃO DE DADOS (LGPD)
                {expandedSection === 'lgpd' ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </h3>
              <div className={expandedSection === 'lgpd' || expandedSection === null ? '' : 'hidden'}>
                <p className="text-slate-700 leading-relaxed">
                  9.1. A CONTRATADA, na qualidade de operadora de dados pessoais, compromete-se a:
                </p>
                <p className="text-slate-700 leading-relaxed mt-2 ml-4">
                  a) Tratar os dados pessoais apenas para as finalidades descritas neste contrato;
                </p>
                <p className="text-slate-700 leading-relaxed mt-2 ml-4">
                  b) Adotar medidas técnicas e administrativas de segurança da informação;
                </p>
                <p className="text-slate-700 leading-relaxed mt-2 ml-4">
                  c) Não compartilhar dados com terceiros sem autorização expressa;
                </p>
                <p className="text-slate-700 leading-relaxed mt-2 ml-4">
                  d) Comunicar incidentes de segurança em até 48 horas;
                </p>
                <p className="text-slate-700 leading-relaxed mt-2 ml-4">
                  e) Eliminar os dados pessoais ao término do contrato, salvo obrigação legal de retenção.
                </p>
                <p className="text-slate-700 leading-relaxed mt-4">
                  9.2. O CONTRATANTE permanece como controlador dos dados pessoais, cabendo-lhe as decisões sobre o tratamento.
                </p>
              </div>
            </section>

            <section className="mb-8">
              <h3 className="text-lg font-bold text-slate-900 mb-4 cursor-pointer flex items-center justify-between" onClick={() => toggleSection('confidencialidade')}>
                CLÁUSULA DÉCIMA - DA CONFIDENCIALIDADE
                {expandedSection === 'confidencialidade' ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </h3>
              <div className={expandedSection === 'confidencialidade' || expandedSection === null ? '' : 'hidden'}>
                <p className="text-slate-700 leading-relaxed">
                  10.1. As partes obrigam-se a manter sigilo sobre todas as informações confidenciais a que tiverem acesso 
                  em razão deste contrato, pelo prazo de 5 (cinco) anos após o término da vigência contratual.
                </p>
                <p className="text-slate-700 leading-relaxed mt-4">
                  10.2. São consideradas informações confidenciais: dados de usuários, documentos, relatórios, metodologias, 
                  processos internos e quaisquer informações não públicas.
                </p>
              </div>
            </section>

            <section className="mb-8">
              <h3 className="text-lg font-bold text-slate-900 mb-4 cursor-pointer flex items-center justify-between" onClick={() => toggleSection('exportacao')}>
                CLÁUSULA DÉCIMA PRIMEIRA - DA EXPORTAÇÃO DE DADOS
                {expandedSection === 'exportacao' ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </h3>
              <div className={expandedSection === 'exportacao' || expandedSection === null ? '' : 'hidden'}>
                <p className="text-slate-700 leading-relaxed">
                  11.1. O CONTRATANTE terá direito à exportação integral de seus dados a qualquer tempo, em formato aberto 
                  e interoperável (JSON, CSV ou XML), sem custos adicionais.
                </p>
                <p className="text-slate-700 leading-relaxed mt-4">
                  11.2. Ao término do contrato, a CONTRATADA disponibilizará todos os dados do CONTRATANTE em até 30 (trinta) 
                  dias, mantendo-os armazenados por mais 90 (noventa) dias para eventual recuperação.
                </p>
              </div>
            </section>

            <section className="mb-8">
              <h3 className="text-lg font-bold text-slate-900 mb-4 cursor-pointer flex items-center justify-between" onClick={() => toggleSection('extincao')}>
                CLÁUSULA DÉCIMA SEGUNDA - DA EXTINÇÃO
                {expandedSection === 'extincao' ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </h3>
              <div className={expandedSection === 'extincao' || expandedSection === null ? '' : 'hidden'}>
                <p className="text-slate-700 leading-relaxed">
                  12.1. O contrato poderá ser extinto nas hipóteses previstas nos arts. 137 a 139 da Lei nº 14.133/2021.
                </p>
                <p className="text-slate-700 leading-relaxed mt-4">
                  12.2. Em caso de extinção antecipada por conveniência da Administração, o CONTRATANTE deverá notificar 
                  a CONTRATADA com antecedência mínima de 60 (sessenta) dias.
                </p>
                <p className="text-slate-700 leading-relaxed mt-4">
                  12.3. A extinção não exime as partes das obrigações de confidencialidade e proteção de dados.
                </p>
              </div>
            </section>

            <section className="mb-8">
              <h3 className="text-lg font-bold text-slate-900 mb-4 cursor-pointer flex items-center justify-between" onClick={() => toggleSection('foro')}>
                CLÁUSULA DÉCIMA TERCEIRA - DO FORO
                {expandedSection === 'foro' ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </h3>
              <div className={expandedSection === 'foro' || expandedSection === null ? '' : 'hidden'}>
                <p className="text-slate-700 leading-relaxed">
                  13.1. Fica eleito o foro da sede do CONTRATANTE para dirimir quaisquer questões oriundas deste contrato, 
                  com renúncia expressa a qualquer outro, por mais privilegiado que seja.
                </p>
              </div>
            </section>

            {/* Assinaturas */}
            <section className="mt-16 pt-8 border-t">
              <p className="text-slate-700 text-center mb-12">
                E, por estarem assim justas e contratadas, as partes assinam o presente instrumento em 3 (três) vias de 
                igual teor e forma, para um só efeito, na presença de 2 (duas) testemunhas.
              </p>
              <p className="text-slate-700 text-center mb-12">
                [LOCAL], {today}.
              </p>
              
              <div className="grid grid-cols-2 gap-16 mt-16">
                <div className="text-center">
                  <div className="border-t border-slate-300 pt-4">
                    <p className="font-bold">{orgName || '[NOME DO ÓRGÃO]'}</p>
                    <p className="text-sm text-slate-500">{representante || '[REPRESENTANTE]'}</p>
                    <p className="text-sm text-slate-500">{cargo || '[CARGO]'}</p>
                    <p className="text-sm text-slate-500">CONTRATANTE</p>
                  </div>
                </div>
                <div className="text-center">
                  <div className="border-t border-slate-300 pt-4">
                    <p className="font-bold">BLUEGOV TECNOLOGIA LTDA.</p>
                    <p className="text-sm text-slate-500">[Nome do Representante]</p>
                    <p className="text-sm text-slate-500">Diretor</p>
                    <p className="text-sm text-slate-500">CONTRATADA</p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-16 mt-16">
                <div className="text-center">
                  <div className="border-t border-slate-300 pt-4">
                    <p className="text-sm text-slate-500">Testemunha 1</p>
                    <p className="text-sm text-slate-400">Nome:</p>
                    <p className="text-sm text-slate-400">CPF:</p>
                  </div>
                </div>
                <div className="text-center">
                  <div className="border-t border-slate-300 pt-4">
                    <p className="text-sm text-slate-500">Testemunha 2</p>
                    <p className="text-sm text-slate-400">Nome:</p>
                    <p className="text-sm text-slate-400">CPF:</p>
                  </div>
                </div>
              </div>
            </section>

            {/* Anexos */}
            <section className="mt-16 pt-8 border-t">
              <h3 className="text-lg font-bold text-slate-900 mb-6">ANEXOS</h3>
              <div className="grid md:grid-cols-2 gap-4">
                {[
                  { num: 'I', title: 'Termo de Prestação de Serviços', desc: 'Detalhamento dos serviços de implantação, treinamento e suporte' },
                  { num: 'II', title: 'Termo de Licenciamento SaaS', desc: 'Condições de uso da plataforma BlueGov AI' },
                  { num: 'III', title: 'Termo de Confidencialidade', desc: 'Acordo de sigilo e não divulgação' },
                  { num: 'IV', title: 'Termo LGPD', desc: 'Acordo de tratamento de dados pessoais' },
                  { num: 'V', title: 'Especificação Técnica', desc: 'Funcionalidades e requisitos técnicos da plataforma' },
                  { num: 'VI', title: 'Matriz de SLA', desc: 'Níveis de serviço e tempos de resposta' },
                  { num: 'VII', title: 'Termo de Transferência de Conhecimento', desc: 'Plano de capacitação e documentação' },
                  { num: 'VIII', title: 'Manual de Onboarding', desc: 'Guia de implantação e primeiros passos' }
                ].map((anexo) => (
                  <div key={anexo.num} className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                        <span className="font-bold text-blue-600">{anexo.num}</span>
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{anexo.title}</p>
                        <p className="text-xs text-slate-500">{anexo.desc}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ContratoPage;
